import { DIOPTRA_REVIEW_ID } from "../../constants";
import { PAGES } from "../../context/PageContext";
import { appendPropertyToDocComments, fetchMultiplePropertyValuesFromDocComments } from "../../utils/document";
import { toastError } from "../../utils/toast";
import { saveActionOutcomeOrToast } from "../../utils/action-outcome-save";
import type { CollectInputAnswer } from "@common/schemas/icertis";
import type { PlaybookOption } from "@common/schemas/playbook-option";
import { playbookChoiceMessage } from "@common/utils/playbookChoiceMessage";
import { saveCollectInputAnswer } from "@common/utils/saveCollectInputAnswer";
import type { WebAppFetch } from "@common/utils/webAppFetch";
import { useCallback } from "react";
import { z } from "zod";
import type { NewUserPrompt, UserPrompt } from "../../utils/web-api";
import type { useAnalytics } from "../../context/AnalyticsContext";
import type { useChat } from "../../context/ChatContext";
import type { ChatScrollTarget } from "src/context/ChatContext";
import type { PromptType } from "src/context/SavedPromptsContext";
import type { useMessageComposer } from "./use-message-composer";

const CreatedRuleResponseSchema = z.object({ id: z.string() });

export interface UseConversationArtifactsInputs extends Pick<
  ReturnType<typeof useChat>,
  "markActionAsApplied" | "markActionAsDismissed" | "mergeMessageTurnArtifacts"
> {
  webappFetch: WebAppFetch;
  sendError: ReturnType<typeof useAnalytics>["sendError"];
  createPrompt: (type: PromptType, data: NewUserPrompt) => Promise<UserPrompt | null>;
  handleSend: ReturnType<typeof useMessageComposer>["handleSend"];
  applyActiveReview: (reviewId: string | null) => Promise<void>;
  setPendingScrollTarget: (target: ChatScrollTarget) => void;
  showPage: (pageName: string, params?: Record<string, any>) => void;
  conversationId: string;
}

export function useConversationArtifacts({ webappFetch, markActionAsApplied, sendError, markActionAsDismissed, mergeMessageTurnArtifacts, createPrompt, handleSend, applyActiveReview, setPendingScrollTarget, showPage, conversationId }: UseConversationArtifactsInputs) {
  const handleDismissSuggestion = useCallback((suggestionId: string) => {
    void saveActionOutcomeOrToast(() => markActionAsDismissed(conversationId, suggestionId));
  }, [markActionAsDismissed, conversationId]);
  const handleSaveCollectInputAnswer = useCallback(async (messageId: string, toolCallId: string, answer: CollectInputAnswer) => {
    try {
      if (!conversationId) throw new Error("No conversation selected");
      await saveCollectInputAnswer(webappFetch, conversationId, toolCallId, answer);
      mergeMessageTurnArtifacts(messageId, { answer });
    } catch (error) {
      toastError(error);
      sendError(error instanceof Error ? error : new Error(String(error)), {
        component: "ChatPage",
        action: "save_collect_input_answer",
        conversationId,
      });
      throw error;
    }
  }, [mergeMessageTurnArtifacts, conversationId, webappFetch, sendError]);
  const handleSaveGuideline = useCallback(async (suggestionId: string, name: string, description: string, category: string, rule: string, comment: string, importance: string, playbookId: string) => {
    try {
      const newRule = CreatedRuleResponseSchema.parse(await webappFetch("/api/user-rules", {
        method: "POST",
        body: JSON.stringify({
          name: name,
          description: description,
          category: category,
          rule: rule,
          type: "guideline",
          importance: importance,
          remedy: `- comment_once ${comment}`
        })
      }));

      await webappFetch("/api/playbook-rules", {
        method: "POST",
        body: JSON.stringify({
          userPlaybookId: playbookId,
          userRuleId: newRule.id
        })
      });

      markActionAsApplied(conversationId, suggestionId);

    } catch (error) {
      toastError(error);
      sendError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ChatPage',
        action: 'save_guideline',
        conversationId
      });
    }
  }, [conversationId, webappFetch, sendError, markActionAsApplied]);
  const handleSaveSkill = useCallback(async (suggestionId: string, name: string, description: string, content: string) => {
    try {
      await createPrompt("skill", {
        name,
        content,
        description,
        type: "skill",
      });
      markActionAsApplied(conversationId, suggestionId);
    } catch (error) {
      toastError(error);
      sendError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ChatPage',
        action: 'save_skill',
        conversationId
      });
    }
  }, [conversationId, createPrompt, sendError, markActionAsApplied]);
  const handlePlaybookSelect = useCallback(async (messageId: string, chosenPlaybook: PlaybookOption) => {
    const userMessage = playbookChoiceMessage(chosenPlaybook);
    await handleSend(userMessage, undefined);

    markActionAsApplied(conversationId, messageId);
  }, [handleSend, markActionAsApplied, conversationId]);
  const handleCreateNewPlaybook = useCallback(async (messageId: string) => {
    const userMessage = "I want to create a new playbook";
    await handleSend(userMessage, undefined);

    markActionAsApplied(conversationId, messageId);
  }, [handleSend, markActionAsApplied, conversationId]);
  const handleOpenReview = useCallback(async (reviewId: string, fromActionId: string) => {
    async function registerReviewOnDocument() {
      const embeddedIds = await fetchMultiplePropertyValuesFromDocComments(DIOPTRA_REVIEW_ID);
      if (!embeddedIds.includes(reviewId)) {
        await appendPropertyToDocComments(`${DIOPTRA_REVIEW_ID}=${reviewId}`);
      }
    }
    try {
      await registerReviewOnDocument();
    } catch (error) {
      console.error("Failed to register review id on the document:", error);
    }
    await applyActiveReview(reviewId);
    if (conversationId) {
      setPendingScrollTarget({ conversationId, actionId: fromActionId });
    }
    showPage(PAGES.documentReviewPage);
  }, [applyActiveReview, showPage, conversationId, setPendingScrollTarget]);
  return { handleSaveCollectInputAnswer, handleSaveGuideline, handleDismissGuideline: handleDismissSuggestion, handleSaveSkill, handleDismissSkill: handleDismissSuggestion, handlePlaybookSelect, handleCreateNewPlaybook, handleOpenReview };
}
